import { first } from "rxjs/operators";
import { ContainerInstance, Service } from "typedi";
import {
  MESSAGE_BUS_TOKEN,
  MESSAGE_SUBSCRIBER_TOKEN,
  STORE_TOKEN,
  TRANSLATOR_TOKEN,
} from "../../shared/dependency-injection/InjectionTokens";

import { PUBLIC_EVENTS } from "../../application/core/models/constants/EventTypes";
import { MERCHANT_PARENT_FRAME } from "../../application/core/models/constants/Selectors";
import { InternalsMonitor } from "../../application/core/services/monitoring/InternalsMonitor";
import { NonControlFrameInternalsMonitorFactory } from "../../application/core/services/monitoring/NonControlFrameInternalsMonitorFactory";
import { IMessageBus } from "../../application/core/shared/message-bus/IMessageBus";
import { IConfig } from "../../shared/model/config/IConfig";
import { FrameIdentifier } from "../../shared/services/message-bus/FrameIdentifier";
import { FramesHub } from "../../shared/services/message-bus/FramesHub";
import { InterFrameCommunicator } from "../../shared/services/message-bus/InterFrameCommunicator";
import { MessageSubscriberRegistry } from "../../shared/services/message-bus/MessageSubscriberRegistry";
import { ofType } from "../../shared/services/message-bus/operators/ofType";
import { BrowserLocalStorage } from "../../shared/services/storage/BrowserLocalStorage";
import { ST } from "../st/ST";

@Service()
export class ClientBootstrap {
  private isAlreadyRunning = false;

  constructor(
    private frameIdentifier: FrameIdentifier,
    private container: ContainerInstance,
  ) {}

  run(config: IConfig): ST {
    if (this.isAlreadyRunning) {
      // REVIEW: This is never executed. Always false.
      // InternalsMonitor.getInstance check works.
      this.container.get(ST).destroy();
      console.warn(
        "The current instance of ST has been destroyed as a result of starting ST again",
      );
    }

    this.frameIdentifier.setFrameName(MERCHANT_PARENT_FRAME);
    const isFirstMonitorSetup = !InternalsMonitor.hasInstance();

    if (isFirstMonitorSetup) {
      // Configure the internal monitor for the component.
      InternalsMonitor.setInstance(
        this.container
          .get(NonControlFrameInternalsMonitorFactory)
          .create(this.frameIdentifier),
      );

      this.container.set("IInternalsMonitor", InternalsMonitor.getInstance());

      window.addEventListener("error", (error: ErrorEvent) => {
        // TODO: Review if we can enable this. Currently we cannot as it would capture
        // any error on the webpage. Note that Sentry already does this.
        // InternalsMonitor.getInstance().recordIssue(error.error);
      });
    }

    this.isAlreadyRunning = true;

    this.container.get(InterFrameCommunicator).init();
    this.container.get(FramesHub).init();
    this.container.get(MESSAGE_BUS_TOKEN);
    this.container.get(STORE_TOKEN);
    this.container.get(BrowserLocalStorage).init();
    this.container
      .get(MessageSubscriberRegistry)
      .register(...this.container.getMany(MESSAGE_SUBSCRIBER_TOKEN));
    this.container.get(TRANSLATOR_TOKEN).init();

    const st: ST = this.container.get(ST);
    const messageBus: IMessageBus = this.container.get(MESSAGE_BUS_TOKEN);

    st.init(config);

    messageBus.pipe(ofType(PUBLIC_EVENTS.DESTROY), first()).subscribe(() => {
      this.isAlreadyRunning = false;
    });

    return st;
  }
}
